/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* eslint-disable react/prop-types,react/no-unused-state */
import ImportIcon from '@mui/icons-material/esm/CloudDownload';
import ExportIcon from '@mui/icons-material/esm/CloudUpload';
import CopyIcon from '@mui/icons-material/esm/ContentCopy';
import DeleteIcon from '@mui/icons-material/esm/Delete';
import MoreVertIcon from '@mui/icons-material/esm/MoreVert';
import OpenInNew from '@mui/icons-material/esm/OpenInNew';
import SettingsIcon from '@mui/icons-material/esm/Settings';
import {Box, Divider, IconButton, Popover, SvgIcon, Table, TableBody, TextField} from '@mui/material';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { withStyles } from '@mui/styles';
// import Menu from 'material-ui-popup-state/HoverMenu';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import { bindActionCreators } from 'redux';
import * as Actions from '../../actions/actions';
import { formatDateString } from '../../components/base/listing/Utils';
import { IconPlay, IconStop } from '../../components/icons';
import Constants from '../../constants/Constants';
import { intl } from '../../helper/IntlGlobalProvider';
import { Path } from '../../helper/Path';
import theme from '../../theme';
import { withUser } from '../../UserProvider';
import APPS_CHANGES_SUBSCRIPTION from '../app/app-changes.gql';
import AppDeleteDialog from '../app/AppDeleteDialog';
import { addToFavorites, duplicateApp, removeFromFavorites } from '../app/AppGraphQL';
import { AppSettingsApps } from '../app/AppSettingsApps';
import { runQuery, useSubscription } from '../app/GraphQLWSClient';
import NewAppDialog from '../app/NewAppDialog';
import { ImportDropzone } from '../importexport/ImportDropzone';
import { StartImportDialog } from '../importexport/StartImportDialog';
import { StreamSheetAppBar } from '../StreamSheetAppBar';
import StreamSheetNavigation from '../StreamSheetNavigation';
import CustomTooltip from '../utils/CustomTooltip';
import ServerStatusDialog from '../utils/ServerStatusDialog';
import StreamSheetBreadCrumbs from '../utils/StreamSheetBreadCrumbs';
import StreamSheetPageTitle from '../utils/StreamSheetPageTitle';
import StreamSheetPaper from '../utils/StreamSheetPaper';
import TableSortHeader from '../utils/TableSortHeader';
import qs from 'query-string';
import { AppAuth } from '../utils/Authorization';
import FavoriteIcon from '@mui/icons-material/esm/Favorite';
import {getHash} from '../utils/URLHash';
import { buildErrorInfo, ErrorDialog } from '../importexport/ErrorDialog';

const drawerWidth = 60;
const DASHBOARD_QUERY = `
query Machines {
	machines {
		name
		id
		canEdit
		metadata {
			lastModified
			owner
		}
		streamsheets {
			name
		}
		scope {
			id
		}
		state
		settings {
			description
		}
	}
}
`;
const styles = {
	cellContentContainer: {
		display: 'flex',
		justifyContent: 'space-between',
		// width: '200px',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis'
	},
	tablePaperContainer: {
		maxHeight: '100%',
		padding: '24px',
		overflow: 'auto',
		boxSizing: 'border-box'
	},
	tableContainer: {
		padding: '32px',
		maxHeight: '100%',
		maxWidth: '960px',
		margin: 'auto',
		position: 'relative'
	}
};

const useExperimental = (setAppState) => {
	useEffect(() => setAppState({ experimental: localStorage.getItem('experimental') === 'true' }), []);
};

const canAddApp = (scopes, currentScope) =>
	currentScope === 'allWS' || currentScope === 'allWSGrouped'
		? AppAuth.canAddAny(scopes)
		: AppAuth.canAdd(scopes, currentScope);

function AppsPageComponent(props) {
	const getValidWorkspace = () => {
		const ws = localStorage.getItem('workspace');
		if (!ws) {
			return 'allWS';
		}
		if (ws === 'allWS' || ws === 'allWSGrouped') {
			return ws;
		}
		const scope = props.scopes.find((scop) => scop.id === ws);
		return scope ? ws : 'allWS';
	};

	const navigate = useNavigate();
	const location = useLocation();
	const [sortBy, setSortBy] = useState('lastModified');
	const [apps, setApps] = useState(undefined);
	const [selectedApp, setSelectedApp] = useState();
	const [anchor, setAnchor] = useState(null);
	const [filter, setFilter] = useState();
	const [selectedResource, setSelectedResource] = useState();
	const [sortDirection, setSortDirection] = useState('desc');
	const [showAppDeleteDialog, setShowAppDeleteDialog] = useState(false);
	const [showNewAppDialog, setShowNewAppDialog] = useState(getHash().panel === '/New/');
	const [showImportDialog, setShowImportDialog] = useState(false);
	const [showProperties, setShowProperties] = useState(false);
	const [workSpace, setWorkSpace] = useState(getValidWorkspace());
	const { classes } = props;
	const showScopes = props.scopes.length > 1;
	const scopeNames = useMemo(
		() => props.scopes.reduce((acc, cur) => ({ ...acc, [cur.id]: cur.name }), {}),
		[props.scopes]
	);
	const [errorInfo, setErrorInfo] = useState(null);
	const setError = (title, message) => setErrorInfo(buildErrorInfo(title, message));

	// Switch to allWS view if currently selected workspace gets deleted
	useEffect(() => setWorkSpace(getValidWorkspace()), [props.scopes]);

	useEffect(() => {
		setShowNewAppDialog(getHash().panel === '/New/');
	}, [location.hash]);

	// const [filter, setFilter] = useState('');
	useExperimental(props.setAppState);

	useEffect(() => {
		const parseAbleHash = location.hash ? location.hash.substring(1) : '';
		const hash = qs.parse(parseAbleHash);
		if (hash.ws) {
			const ws = hash.ws.replace(/\//gi, '');
			setWorkSpace(ws);
		}
	}, [location.hash]);

	useEffect(() => {
		const run = async () => {
			const result = await runQuery(DASHBOARD_QUERY);
			setApps(result.machines);
		};
		run();
	}, []);

	const [subData] = useSubscription(APPS_CHANGES_SUBSCRIPTION);
	useEffect(() => {
		if (subData) {
			const eventData = subData.appChanged;
			switch (eventData.__typename) {
				case 'AppCreateEvent': {
					setApps((current) => [...current, eventData.app]);
					break;
				}
				case 'AppDeleteEvent': {
					setApps((current) => current.filter((app) => app.id !== eventData.appId));
					break;
				}
				case 'AppUpdateEvent': {
					const { appId, scope, lastModified, description, ...changes } = eventData;
					const updateApp = (app) => {
						const metadataUpdate =
							lastModified !== null ? { metadata: { ...app.metadata, lastModified } } : {};
						const settingsUpdate =
							description !== null ? { settings: { ...app.settings, description } } : {};
						const validChanges = Object.fromEntries(Object.entries(changes).filter(([, v]) => v !== null));
						return { ...app, ...metadataUpdate, ...settingsUpdate, ...validChanges };
					};
					setApps((current) => current.map((app) => (app.id === appId ? updateApp(app) : app)));
					break;
				}
				default:
			}
		}
	}, [subData]);

	const handleSelection = (resource, newWindow = false) => {
		const path = Path.app(resource.id);
		if (newWindow) {
			window.open(path, '_blank');
		} else {
			navigate(path);
		}

		const getInfo = (pos) => {
			const lru = {
				data: localStorage.getItem(`LRU${pos + 1}`)
			};

			if (!lru.data) {
				return undefined;
			}

			const info = lru.data.split(';');
			lru.id = info[0];
			return lru;
		};

		const lrus = [];

		for (let i = 0; i < 3; i += 1) {
			const info = getInfo(i);
			if (info) {
				lrus.push(info);
			}
		}

		const curIdx = lrus.findIndex((lru) => lru.id === resource.id);
		if (curIdx === -1) {
			localStorage.setItem('LRU1', `${resource.id};0;0;0`);
			if (lrus[0]) {
				localStorage.setItem('LRU2', lrus[0].data);
			}
			if (lrus[1]) {
				localStorage.setItem('LRU3', lrus[1].data);
			}
		} else {
			localStorage.setItem('LRU1', lrus[curIdx].data);
			localStorage.setItem(`LRU${curIdx + 1}`, lrus[0].data);
		}
	};

	const handleWorkSpaceChange = (ws) => {
		setWorkSpace(ws);
		localStorage.setItem('workspace', ws);
	};

	const handleMenuSelect = async (event, optionIndex, resource) => {
		// const findMachineToActivate = (id) => {
		// 	const machine = apps.find((m) => m.id === id);
		// 	return { ...machine, progressing: !machine.progressing };
		// };
		switch (optionIndex) {
			case Constants.RESOURCE_MENU_IDS.SETTINGS: {
				setSelectedApp(resource.id);
				setShowProperties(true);
				break;
			}
			case Constants.RESOURCE_MENU_IDS.CLONE: {
				try {
					// const newAppId =
					await duplicateApp(resource.id);
				} catch (error) {
					setError('AppsPage.Duplicate', 'License.StreamsheetsMaxReached');
				}
				break;
			}
			case Constants.RESOURCE_MENU_IDS.OPEN:
				window.open(Path.app(resource.id), '_blank');
				break;
			case Constants.RESOURCE_MENU_IDS.DELETE: {
				const machine = apps.find((m) => m.id === resource.id);
				setShowAppDeleteDialog({ id: machine.id, name: machine.name });
				break;
			}
			// case Constants.RESOURCE_MENU_IDS.SET_TITLE_IMAGE: {
			// 	const machine = apps.find((m) => m.id === resource.id);
			// 	this.setState({
			// 		dialogMachineTitleImageOpen: true,
			// 		currentMachine: machine
			// 	});
			// 	props.openDashboard(machine);
			// 	break;
			// }
			// case Constants.RESOURCE_MENU_IDS.RESET_TITLE_IMAGE: {
			// 	const machine = apps.find((m) => m.id === resource.id);
			// 	props.setTitleImage(machine, toDataURL(this.defaultTitleImage.current));
			// 	const { openDashboard } = props;
			// 	setTimeout(() => {
			// 		openDashboard(machine.id);
			// 	}, 500)
			// 	break;
			// }
			case Constants.RESOURCE_MENU_IDS.START: {
				props
					.start(resource.id)
					.then(() => {
						navigate(`/apps`);
					})
					.catch((error) => {
						console.error(error);
					});
				break;
			}
			case Constants.RESOURCE_MENU_IDS.STOP: {
				props
					.stop(resource.id)
					.then(() => navigate('/apps'))
					.catch((/* error */) => {
						// TODO: handle error
					});
				break;
			}
			case Constants.RESOURCE_MENU_IDS.PAUSE: {
				props
					.pause(resource.id)
					.then(() => navigate('/apps'))
					.catch((/* error */) => {
						// TODO: handle error
					});
				break;
			}
			case Constants.RESOURCE_MENU_IDS.EXPORT: {
				// this.forceUpdate();
				navigate(Path.export(resource.id));
				break;
			}
			default:
		}

		setAnchor(null);

		event.preventDefault();
		event.stopPropagation();
	};

	const getMachines = (ws) => {
		let rows = [];

		Object.assign(rows, apps);

		if (filter && filter.length > 0) {
			rows = rows.filter((resource) => resource.name.toLowerCase().includes(filter.toLowerCase()));
		}
		if (!ws.includes('allWS')) {
			rows = rows.filter((resource) => resource.scope.id === ws);
		}

		rows.forEach((row) => {
			row.lastModified = row.metadata.lastModified || Date.now();
			row.lastModifiedFormatted = formatDateString(new Date(row.lastModified).toISOString());
			row.description = row.settings.description ? row.settings.description : '';
			row.favorite = props.favorites.find((fav) => fav === row.id);
		});

		rows.sort((a, b) => {
			const dir = sortDirection === 'asc' ? 1 : -1;

			switch (sortBy) {
				case 'fav':
					return (!!b.favorite - !!a.favorite) * dir;
				case 'description':
				case 'name': {
					const aName = a[sortBy] || '';
					const bName = b[sortBy] || '';
					if (aName.toLowerCase() > bName.toLowerCase()) {
						return dir;
					} else if (aName.toLowerCase() < bName.toLowerCase()) {
						return -1 * dir;
					}
					return 0;
				}
				case 'action': {
					const aState = a.state || '';
					const bState = b.state || '';
					const stateValues = {
						running: 0,
						paused: 1,
						stopped: 2
					};
					if (stateValues[aState] > stateValues[bState]) {
						return dir;
					} else if (stateValues[aState] < stateValues[bState]) {
						return -1 * dir;
					}
					return 0;
				}
				case 'lastModified': {
					const aLastModified = a.lastModified || new Date().toISOString();
					const bLastModified = b.lastModified || new Date().toISOString();
					const res = new Date(aLastModified) - new Date(bLastModified);
					return dir * res;
				}
				case 'scope': {
					const aId = a[sortBy].id || '';
					const bId = b[sortBy].id || '';
					if (aId > bId) {
						return dir;
					} else if (aId < bId) {
						return -1 * dir;
					}
					return 0;
				}
				default:
					return 0;
			}
		});

		return rows;
	};

	const setFavorite = async (favorite, appId) => {
		try {
			// setFavorite(!isFavorite);
			if (favorite) {
				await removeFromFavorites(appId);
			} else {
				await addToFavorites(appId);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const handleOpenMenu = (event, resource) => {
		setAnchor(event.currentTarget);
		setSelectedResource(resource);
	};

	const handleCloseMenu = () => {
		setAnchor(null);
		setSelectedResource(undefined);
	};

	const handleSort = (event, property) => {
		const orderBy = property;
		const order = (sortBy === property && sortDirection === 'desc') || sortBy !== property ? 'asc' : 'desc';

		// localStorage.setItem(PREF_KEY_SORTSTREAMS, `${orderBy}_${order}`);

		setSortBy(orderBy);
		setSortDirection(order);
	};

	const handleFilterChange = (newFilter) => {
		setFilter(newFilter);
	};

	const getMachineTable = (ws) => {
		return getMachines(ws).map((resource, index) => (
			<TableRow
				style={{
					height: '40px',
					cursor: 'pointer'
				}}
				hover
				data-tour={index === 0 ? 'apps-open' : undefined}
				tabIndex={-1}
				key={`${resource.className}-${resource.id}`}
			>
				<TableCell onClick={() => handleSelection(resource)} padding="none">
					{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
					<div
						onClick={() => handleSelection(resource)}
						style={{
							marginLeft: '9px',
							marginRight: '5px'
						}}
					>
						<IconButton
							onClick={(event) => {
								// const state = props.favorites.find(fav => fav === resource.id);
								setFavorite(resource.favorite, resource.id);
								event.stopPropagation();
							}}
							style={{ padding: '4px' }}
							size="small"
						>
							{resource.favorite ? (
								<FavoriteIcon
									sx={{
										color: theme.palette.primary.main
									}}
								/>
							) : (
								<SvgIcon>
									<path d="M5,4H19A2,2 0 0,1 21,6V18A2,2 0 0,1 19,20H5A2,2 0 0,1 3,18V6A2,2 0 0,1 5,4M5,8V12H11V8H5M13,8V12H19V8H13M5,14V18H11V14H5M13,14V18H19V14H13Z" />
								</SvgIcon>
							)}
						</IconButton>
					</div>
				</TableCell>
				<TableCell
					style={{ fontWeight: 'bold' }}
					onClick={() => handleSelection(resource)}
					padding="none"
					component="th"
					scope="row"
				>
					<div className={classes.cellContentContainer}>
						<div style={{ lineHeight: '29px' }}>{resource.name}</div>
						<IconButton
							style={{ padding: '4px', marginRight: '25px' }}
							size="small"
							onClick={(event) => handleMenuSelect(event, Constants.RESOURCE_MENU_IDS.OPEN, resource)}
						>
							<OpenInNew />
						</IconButton>
					</div>
				</TableCell>
				{showScopes && workSpace !== 'allWSGrouped' ? (
					<TableCell
						sx={{
							display: {
								xs: 'none',
								sm: 'none',
								md: 'table-cell',
								lg: 'table-cell',
								xl: 'table-cell'
							}
						}}
						onClick={() => handleSelection(resource)}
						padding="none"
					>
						{scopeNames[resource.scope.id]}
					</TableCell>
				) : null}
				<TableCell
					sx={{
						display: {
							xs: 'none',
							sm: 'none',
							md: 'none',
							lg: 'table-cell',
							xl: 'table-cell'
						}
					}}
					onClick={() => handleSelection(resource)}
					padding="none"
				>
					{resource.description}
				</TableCell>
				<TableCell
					sx={{
						display: {
							xs: 'none',
							sm: 'none',
							md: 'table-cell',
							lg: 'table-cell',
							xl: 'table-cell'
						}
					}}
					onClick={() => handleSelection(resource)}
					padding="none"
				>
					{resource.lastModifiedFormatted}
				</TableCell>
				<TableCell data-tour={index === 0 ? 'apps-actions' : undefined} padding="none">
					{!resource.canEdit
						? null
						: [
								<IconButton
									key="start"
									style={{ padding: '4px', marginLeft: '-10px' }}
									size="small"
									disabled={resource.state === 'running'}
									onClick={(event) =>
										handleMenuSelect(event, Constants.RESOURCE_MENU_IDS.START, resource)
									}
								>
									<IconPlay />
								</IconButton>,
								<IconButton
									key="stop"
									style={{ padding: '4px' }}
									size="small"
									disabled={resource.state === 'stopped'}
									onClick={(event) =>
										handleMenuSelect(event, Constants.RESOURCE_MENU_IDS.STOP, resource)
									}
								>
									<IconStop />
								</IconButton>,
								<IconButton
									key="menu"
									style={{ padding: '4px' }}
									size="small"
									onClick={(event) => handleOpenMenu(event, resource)}
								>
									<MoreVertIcon />
								</IconButton>
						  ]}
				</TableCell>
			</TableRow>
		));
	};

	document.title = intl.formatMessage({ id: 'TabTitle.Apps' }, {});
	const wsTitle = intl.formatMessage({ id: 'AppsPage.Workspace' }, {});
	const canAddAppInCurrentWorkspace = canAddApp(props.scopes, workSpace);

	return (
		<div style={{ width: 'inherit', height: 'inherit' }}>
			<ImportDropzone disabled={!canAddAppInCurrentWorkspace} scopeId={workSpace}>
				<div>
					<StreamSheetAppBar
						key="appPageBar"
						helpContext="overview/apps"
						showFilter
						onFilterChange={handleFilterChange}
					/>
					<StreamSheetNavigation openPage={navigate} />
				</div>
				<Box
					sx={{
						position: 'relative',
						height: 'calc(100% - 50px)',
						width: `calc(100% - ${drawerWidth}px)`,
						marginLeft: `${drawerWidth}px`,
						overflow: 'hidden',
						bgcolor: theme.wall.backgroundColor
					}}
				>
					<div
						style={{
							display: 'inline-flex',
							width: 'calc(100% - 15px)',
							justifyContent: 'space-between'
						}}
					>
						<StreamSheetBreadCrumbs path="Home/Apps" openPage={navigate} />
						<div
							style={{
								alignItems: 'end',
								display: 'flex',
								minHeight: '10px',
								marginTop: '5px'
							}}
						>
							{canAddAppInCurrentWorkspace ? (
								<div
									style={{
										display: 'flex'
									}}
								>
									<CustomTooltip header="Tooltip.ImportApps" message="Tooltip.ImportAppsMessage">
										<IconButton onClick={() => setShowImportDialog(true)} size="small">
											<ImportIcon />
										</IconButton>
									</CustomTooltip>
									<CustomTooltip
										header="Tooltip.AddStreamSheet"
										message="Tooltip.AddStreamSheetMessage"
									>
										<div data-tour="apps-add">
											<IconButton
												aria-label="Stop"
												onClick={() => setShowNewAppDialog(true)}
												size="small"
											>
												<SvgIcon>
													<path d="M20 14H14V20H10V14H4V10H10V4H14V10H20V14Z" />
												</SvgIcon>
											</IconButton>
										</div>
									</CustomTooltip>
									<NewAppDialog
										open={showNewAppDialog}
										openPage={navigate}
										workSpace={workSpace}
										onClose={() => setShowNewAppDialog(false)}
									/>
									<StartImportDialog
										open={showImportDialog}
										onClose={() => setShowImportDialog(false)}
									/>
								</div>
							) : null}
						</div>
					</div>
					<StreamSheetPaper sx={{}}>
						<Box
							sx={{
								height: 'calc(100%)',
								width: showProperties ? 'calc(100% - 342px)' : 'calc(100% - 32px)'
							}}
							data-tour="page-apps"
						>
							<div
								style={{
									height: 'calc(100%)',
									display: apps ? 'grid' : 'block',
									gridTemplateRows: 'max-content max-content auto'
								}}
							>
								<StreamSheetPageTitle
									title={<FormattedMessage id="AppsPage.Title" defaultMessage="Apps" />}
									subTitle={
										<FormattedMessage
											id="AppsPage.TitleSub"
											defaultMessage="List of all apps. Click on app to open it or create a new one."
										/>
									}
									select={
										workSpace && props.scopes.length > 1 ? (
											<TextField
												variant="outlined"
												size="small"
												margin="normal"
												select
												fullWidth
												value={workSpace}
												onChange={(event) => handleWorkSpaceChange(event.target.value)}
												label={
													<FormattedMessage
														id="Workspace.Title"
														defaultMessage="Workspaces"
													/>
												}
												data-tour="apps-workspace"
											>
												<MenuItem value="allWS" key="allWS">
													<FormattedMessage id="Workspace.All" defaultMessage="Show All" />
												</MenuItem>
												<MenuItem value="allWSGrouped" key="allWSGrouped">
													<FormattedMessage
														id="Workspace.Grouped"
														defaultMessage="Show All by Workspace"
													/>
												</MenuItem>
												{Object.entries(scopeNames).map(([key, name]) => (
													<MenuItem value={key} key={key}>
														{name}
													</MenuItem>
												))}
											</TextField>
										) : null
									}
								/>
								{apps ? (
									<TableContainer>
										<Table stickyHeader size="medium" aria-label="collapsible table">
											<TableSortHeader
												key="sortheader"
												height={48}
												cells={[
													{
														id: 'fav',
														numeric: false,
														padding: false,
														label: '',
														width: '1%',
														minWidth: '16px'
													},
													{
														id: 'name',
														numeric: false,
														padding: false,
														label: 'AppsPage.Name',
														width: '25%'
													},
													{
														id: 'scope',
														hideOnSmallDisplays: 'lg',
														numeric: false,
														padding: false,
														label: 'AppsPage.Workspace',
														component:
															!showScopes ||
															(workSpace === 'allWSGrouped' && <React.Fragment />),
														width: '15%'
													},
													{
														id: 'description',
														hideOnSmallDisplays: true,
														numeric: false,
														label: 'AppsPage.Description',
														width: '35%'
													},
													{
														id: 'lastModified',
														hideOnSmallDisplays: 'lg',
														numeric: false,
														label: 'AppsPage.LastModified',
														width: '17%'
													},
													{
														id: 'action',
														numeric: false,
														padding: false,
														label: 'AppsPage.Actions',
														width: '15%',
														minWidth: '150px'
													}
												]}
												orderBy={sortBy}
												order={sortDirection}
												onRequestSort={handleSort}
												// onFieldToggle={(field, state) => this.onFieldToggle(field, state)}
											/>
											{workSpace === 'allWSGrouped' ? (
												Object.entries(scopeNames).map(([key, name]) => (
													<TableBody key={key}>
														<TableRow>
															<TableCell
																colSpan={5}
																style={{
																	padding: '11px 0px 11px 7px',
																	fontSize: '12pt',
																	fontWeight: 'bold'
																}}
															>
																{`${wsTitle} ${name}`}
															</TableCell>
														</TableRow>
														{getMachineTable(key)}
													</TableBody>
												))
											) : (
												<TableBody key="apptable">{getMachineTable(workSpace)}</TableBody>
											)}
										</Table>
									</TableContainer>
								) : (
									<ServerStatusDialog height={70} open />
								)}
								<Popover
									anchorEl={anchor}
									id="app-menu"
									open={Boolean(anchor)}
									onClose={() => handleCloseMenu()}
									onClick={() => handleCloseMenu()}
									transformOrigin={{ horizontal: 'right', vertical: 'top' }}
									anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
								>
									<MenuItem
										key="appsettings"
										dense
										onClick={(event) =>
											handleMenuSelect(
												event,
												Constants.RESOURCE_MENU_IDS.SETTINGS,
												selectedResource
											)
										}
									>
										<ListItemIcon>
											<SettingsIcon fontSize="default" />
										</ListItemIcon>
										<ListItemText
											primary={
												<FormattedMessage id="AppsPage.Settings" defaultMessage="Settings" />
											}
										/>
									</MenuItem>
									<Divider key="divider2"/>
									<MenuItem
										key="appclone"
										dense
										onClick={(event) =>
											handleMenuSelect(event, Constants.RESOURCE_MENU_IDS.CLONE, selectedResource)
										}
									>
										<ListItemIcon>
											<CopyIcon fontSize="default" />
										</ListItemIcon>
										<ListItemText
											primary={
												<FormattedMessage id="AppsPage.Duplicate" defaultMessage="Duplicate" />
											}
										/>
									</MenuItem>
									<MenuItem
										dense
										key="appdelete"
										onClick={(event) =>
											handleMenuSelect(
												event,
												Constants.RESOURCE_MENU_IDS.DELETE,
												selectedResource
											)
										}
									>
										<ListItemIcon>
											<DeleteIcon fontSize="default" />
										</ListItemIcon>
										<ListItemText
											primary={<FormattedMessage id="AppsPage.Delete" defaultMessage="Delete" />}
										/>
									</MenuItem>
									<Divider key="divider1"/>
									<MenuItem
										dense
										key="appexport"
										onClick={(event) =>
											handleMenuSelect(
												event,
												Constants.RESOURCE_MENU_IDS.EXPORT,
												selectedResource
											)
										}
									>
										<ListItemIcon>
											<ExportIcon fontSize="default" />
										</ListItemIcon>
										<ListItemText
											primary={<FormattedMessage id="AppsPage.Export" defaultMessage="Export" />}
										/>
									</MenuItem>
								</Popover>
								{showAppDeleteDialog ? (
									<AppDeleteDialog
										open
										machineName={showAppDeleteDialog.name}
										machineId={showAppDeleteDialog.id}
										close={() => setShowAppDeleteDialog(false)}
									/>
								) : null}
							</div>
						</Box>
						{showProperties && selectedResource ? (
							<AppSettingsApps
								scopeNames={scopeNames}
								app={apps.find((a) => a.id === selectedApp)}
								open
								onClose={() => setShowProperties(false)}
							/>
						) : null}
						{errorInfo ? <ErrorDialog  {...errorInfo} open onClose={() => setErrorInfo(null)} /> : null}
					</StreamSheetPaper>

				</Box>
			</ImportDropzone>
		</div>
	);
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators({ ...Actions }, dispatch);
}

export const AppsPage = withUser(({ user }) => ({ favorites: user.favorites, scopes: user.scopes }))(
	injectIntl(connect(null, mapDispatchToProps)(withStyles(styles, { withTheme: true })(AppsPageComponent)))
);
